import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const FIELDS = [
  { key: "site_name", label: "Site Name (ಸೈಟ್ ಹೆಸರು — Kannada)", type: "text" },
  { key: "site_name_english", label: "Site Name (English)", type: "text" },
  { key: "site_tagline", label: "Site Tagline (ಟ್ಯಾಗ್‌ಲೈನ್)", type: "text" },
  { key: "about_text", label: "About Text (ಬಗ್ಗೆ)", type: "textarea" },
  { key: "mission", label: "Mission (ಗುರಿ)", type: "text" },
  { key: "editor_name", label: "Editor Name (ಸಂಪಾದಕರ ಹೆಸರು)", type: "text" },
  { key: "editor_designation", label: "Editor Designation", type: "text" },
  { key: "editor_photo_url", label: "Editor Photo URL", type: "text" },
  { key: "editor_bio", label: "Editor Bio", type: "textarea" },
  { key: "contact_email", label: "Contact Email", type: "text" },
  { key: "contact_phone", label: "Contact Phone", type: "text" },
  { key: "contact_address", label: "Contact Address", type: "textarea" },
  { key: "social_facebook", label: "Facebook URL", type: "text" },
  { key: "social_twitter", label: "Twitter / X URL", type: "text" },
  { key: "social_instagram", label: "Instagram URL", type: "text" },
  { key: "social_youtube", label: "YouTube URL", type: "text" },
  { key: "social_whatsapp", label: "WhatsApp Link (https://wa.me/...)", type: "text" },
  { key: "whatsapp_channel_url", label: "WhatsApp Channel URL (floating bubble)", type: "text" },
  { key: "upi_id", label: "UPI ID (e.g. namaste@ybl)", type: "text" },
  { key: "phonepe_number", label: "PhonePe / GPay Number (+91...)", type: "text" },
  { key: "upi_qr_url", label: "UPI QR Image URL (optional)", type: "text" },
  { key: "payee_name", label: "Payee Name (shown in UPI apps)", type: "text" },
];

export default function AdminSettings() {
  const { user, loading: authLoading } = useAuth();
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  if (authLoading) return <p className="text-muted-foreground">Loading...</p>;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <p className="text-muted-foreground">Admin only.</p>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      await api.put("/settings", payload);
      await refresh();
      toast.success("ಸೆಟ್ಟಿಂಗ್‌ಗಳು ಉಳಿಸಲಾಗಿದೆ");
    } catch {
      toast.error("ಉಳಿಸಲಾಗಲಿಲ್ಲ");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-baseline justify-between border-b-2 border-primary pb-4 mb-8">
        <div>
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2 hover:underline"
            data-testid="settings-back"
          >
            <ArrowLeft className="w-3 h-3" /> Admin
          </Link>
          <div className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground">Settings</div>
          <h1 className="font-kannada-heading text-3xl mt-1">ಸೈಟ್ ಸೆಟ್ಟಿಂಗ್‌ಗಳು</h1>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-none" data-testid="settings-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? "ಉಳಿಸುತ್ತಿದೆ..." : "ಉಳಿಸಿ"}
        </Button>
      </div>

      {/* Editor preview */}
      {form.editor_photo_url && (
        <div className="border border-border p-4 mb-8 bg-card rounded-sm flex items-center gap-4">
          <img
            src={form.editor_photo_url}
            alt=""
            className="w-16 h-16 object-cover rounded-sm border border-border"
          />
          <div>
            <div className="font-kannada-heading text-lg">{form.editor_name || "—"}</div>
            <div className="text-xs uppercase tracking-widest text-primary font-bold">
              {form.editor_designation || "—"}
            </div>
          </div>
          <span className="ml-auto text-xs uppercase tracking-widest text-muted-foreground">
            Live Preview
          </span>
        </div>
      )}

      <form onSubmit={save} className="space-y-5">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-xs uppercase tracking-widest">{f.label}</Label>
            {f.type === "textarea" ? (
              <Textarea
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="rounded-none mt-1"
                rows={3}
                data-testid={`settings-${f.key}`}
              />
            ) : (
              <Input
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                className="rounded-none mt-1"
                data-testid={`settings-${f.key}`}
              />
            )}
          </div>
        ))}
        <Button type="submit" disabled={saving} className="rounded-none" data-testid="settings-save-bottom">
          <Save className="w-4 h-4 mr-1" /> {saving ? "ಉಳಿಸುತ್ತಿದೆ..." : "ಎಲ್ಲಾ ಉಳಿಸಿ"}
        </Button>
      </form>
    </div>
  );
}
